import { IconShape } from '@ads/components-react';
import { InformationCircle, XMark } from '@ads/icons';
import { useIsMutating } from '@tanstack/react-query';
import { atom, useAtom, useSetAtom } from 'jotai';
import { ComponentProps, ComponentType } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ButtonProps } from '../arandu/button';
import { IconButton } from '../arandu/icon-button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';

/**
 * O `IconShape` estica só a largura do ícone — a classe de altura dele tem um typo no design
 * system —, então o desenho sai com 32 de largura e 24 de altura. O tamanho vai fixado por
 * `custom.icon`, com `!` porque a classe do componente vem junto e as duas não se resolvem entre
 * si, e o traço de 2px do protótipo é repassado ao ícone pelo próprio `IconShape`.
 *
 * Vale também para o ícone padrão de cada tipo, que é o caso de `icon: true` e de `icon: 'danger' | 'warning'`.
 */
const DIALOG_ICON = { custom: { icon: '!size-xxs' }, strokeWidth: 2 } as const;

type Hierarchy = 'danger' | 'warning';

type DialogProps = {
  title: string;
  description: string;
  icon?: Hierarchy | boolean | ComponentType<ComponentProps<typeof InformationCircle>>;
  action: {
    title: string;
    cancelTitle?: string;
    cancelHierarchy?: ButtonProps['hierarchy'];
    hierarchy?: ButtonProps['hierarchy'];
    onClick: () => Promise<void> | void;
    onCancel?: () => void;
  };
};

export const dialogAtom = atom<DialogProps | null>(null);

export const useDialog = () => {
  const setDialog = useSetAtom(dialogAtom);

  return {
    open: (options: DialogProps) => {
      setDialog(options);
      document.body.style.pointerEvents = 'auto';
    },
    close: () => {
      setDialog(null);
      // Radix deixa pointer-events: none no body ao fechar por caminho programático (fora do seu próprio
      // ciclo de open/close nativo); sem isso a página inteira para de responder a cliques (ver #24259).
      document.body.style.pointerEvents = 'auto';
    },
  };
};

const isIconHierarchy = (value: unknown): value is Hierarchy => value === 'danger' || value === 'warning';

export const AlertDialogProvider = () => {
  const [dialog, setDialog] = useAtom(dialogAtom);
  const isMutating = useIsMutating() > 0;
  const { t } = useTranslation();

  if (dialog === null) return null;

  const hierarchy = dialog.icon ?? (isIconHierarchy(dialog.action.hierarchy) ? dialog.action.hierarchy : undefined);

  const close = () => {
    setDialog(null);
    document.body.style.pointerEvents = 'auto';
  };

  const renderIcon = () => {
    if (isIconHierarchy(hierarchy)) {
      return <IconShape {...DIALOG_ICON} size='md' type={hierarchy} data-testid='alert-dialog-status-icon' />;
    }
    if (dialog.icon === true || typeof dialog.icon === 'function') {
      if (dialog.action.hierarchy === 'danger') {
        return (
          <IconShape
            {...DIALOG_ICON}
            size='md'
            type='danger'
            icon={typeof dialog.icon === 'function' ? dialog.icon : undefined}
            data-testid='alert-dialog-status-icon'
          />
        );
      }
      return (
        <IconShape
          {...DIALOG_ICON}
          size='md'
          type='brand'
          icon={typeof dialog.icon === 'function' ? dialog.icon : InformationCircle}
          data-testid='alert-dialog-status-icon'
        />
      );
    }
    return null;
  };

  return (
    <AlertDialog open={dialog !== null}>
      <AlertDialogContent className='z-[999] sm:min-w-[37rem]'>
        <AlertDialogHeader className='flex flex-col text-left gap-xxxs'>
          <div className='flex items-start justify-between'>
            {renderIcon()}

            {/* Fechar sem decidir é o mesmo que cancelar: a ação de cancelamento é a que roda. */}
            <IconButton
              className='ml-auto'
              hierarchy='tertiary'
              size='sm'
              ariaLabel={t('close')}
              disabled={isMutating}
              onClick={() => {
                dialog.action.onCancel?.();
                close();
              }}
              icon={() => <XMark size={20} aria-hidden />}
            />
          </div>

          <div className='flex flex-col gap-nano'>
            <AlertDialogTitle>{dialog.title}</AlertDialogTitle>
            <AlertDialogDescription className='whitespace-pre-line'>
              <Trans>{dialog.description}</Trans>
            </AlertDialogDescription>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className='flex gap-mini !mt-xs'>
          <AlertDialogCancel
            disabled={isMutating}
            onClick={() => {
              dialog.action.onCancel?.();
              close();
            }}
            hierarchy={dialog.action.cancelHierarchy ?? 'tertiary'}
          >
            {dialog.action.cancelTitle ?? t('cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isMutating}
            hierarchy={dialog.action.hierarchy ?? 'primary'}
            onClick={async () => {
              await dialog.action.onClick();
              setDialog(null);
            }}
          >
            {dialog.action.title}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
